import { createFileRoute } from "@tanstack/react-router";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";

import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/use-auth";
import { useCurrentLocation } from "@/hooks/use-current-location";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Slider } from "@/components/ui/slider";
import { toast } from "sonner";
import { Clock, Bell, Percent, CalendarDays, Timer, Coins, Flame, FileText } from "lucide-react";
import type { Tables } from "@/integrations/supabase/types";
import { NUDGE_LEAD_OPTIONS, getLocaleDefaults } from "@/lib/onboarding-defaults";
import { AccessGatedAction } from "@/components/AccessGatedAction";
import { RewardQuotaPicker } from "@/components/RewardQuotaPicker";

/**
 * Returns a regenerated string if the current value matches the auto-generated
 * template pattern for ANY percent value. Returns null if the user has customized it.
 */
function regenerateIfTemplated(
  currentValue: string | null | undefined,
  template: string,
  newPercent: number,
): string | null {
  if (!currentValue) return template.replace("{X}", String(newPercent));
  // Build a regex from the template by escaping then replacing {X} with \d+
  const escaped = template.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const pattern = new RegExp("^" + escaped.replace("\\{X\\}", "\\d+") + "$");
  if (pattern.test(currentValue)) {
    return template.replace("{X}", String(newPercent));
  }
  return null;
}

export const Route = createFileRoute("/_authenticated/discounts")({
  component: DiscountsPage,
});

type Merchant = Tables<"merchants">;




function DiscountsPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { current } = useCurrentLocation();
  const [merchant, setMerchant] = useState<Merchant | null>(null);
  const [loading, setLoading] = useState(true);

  // Local state for editable fields
  const [redemptionStyle, setRedemptionStyle] = useState<"quick_burn" | "verified_voucher">("quick_burn");
  const [activationHours, setActivationHours] = useState(0);
  const [nudgeLeadDays, setNudgeLeadDays] = useState(14);
  const [initialDiscount, setInitialDiscount] = useState(10);
  const [rewardExpiryMonths, setRewardExpiryMonths] = useState(3);
  const [rewardQuotaCount, setRewardQuotaCount] = useState(0);
  const [rewardQuotaWindowDays, setRewardQuotaWindowDays] = useState(60);
  const [saving, setSaving] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  // How many open rewards would become due for a reminder if the merchant
  // widens the reminder window. Null = no warning to show.
  const [burstCount, setBurstCount] = useState<number | null>(null);


  const discountChanges = merchant
    ? [
        { key: "initial", label: t("discounts.levels.labels.initial"), from: merchant.promo_discount_percent, to: initialDiscount },
      ].filter((c) => c.from !== c.to)
    : [];

  const applyMerchant = (data: Merchant) => {
    setRedemptionStyle((data as any).redemption_style || "quick_burn");
    setActivationHours(data.promo_activation_hours);
    setNudgeLeadDays((data as any).nudge_lead_days ?? 14);
    setInitialDiscount(data.promo_discount_percent);
    setRewardExpiryMonths((data as any).reward_expiry_months ?? 3);
    setRewardQuotaCount((data as any).reward_quota_count ?? 0);
    setRewardQuotaWindowDays((data as any).reward_quota_window_days ?? 60);
  };

  // Which cards hold unsaved changes
  const dirty = {
    redemption: !!merchant && redemptionStyle !== (((merchant as any).redemption_style as string) || "quick_burn"),
    activation: !!merchant && activationHours !== merchant.promo_activation_hours,
    followup:
      !!merchant && nudgeLeadDays !== (((merchant as any).nudge_lead_days as number) ?? 14),
    levels: !!merchant && initialDiscount !== merchant.promo_discount_percent,
    validity: !!merchant && rewardExpiryMonths !== (((merchant as any).reward_expiry_months as number) ?? 3),
    quota:
      !!merchant &&
      (rewardQuotaCount !== (((merchant as any).reward_quota_count as number) ?? 0) ||
        rewardQuotaWindowDays !== (((merchant as any).reward_quota_window_days as number) ?? 60)),
  };

  const dirtyCount = Object.values(dirty).filter(Boolean).length;

  const ChangedBadge = ({ show }: { show: boolean }) =>
    show ? (
      <span className="ml-2 inline-flex items-center rounded-full bg-primary/10 text-primary px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide align-middle">
        {t("discounts.changedBadge")}
      </span>
    ) : null;

  useEffect(() => {
    if (!user || !current?.id) return;
    setLoading(true);
    supabase
      .from("merchants")
      .select("*")
      .eq("id", current.id)
      .maybeSingle()
      .then(({ data }) => {
        if (data) {
          setMerchant(data);
          applyMerchant(data);
        }
        setLoading(false);
      });
  }, [user, current?.id]);

  // Widening the reminder window makes every open reward inside the new window
  // due at once. Count them so the merchant sees the SMS spend before saving.
  useEffect(() => {
    const savedLead = ((merchant as any)?.nudge_lead_days as number) ?? 14;
    if (!merchant || rewardExpiryMonths === 0 || nudgeLeadDays <= savedLead) {
      setBurstCount(null);
      return;
    }
    let cancelled = false;
    const nowIso = new Date().toISOString();
    const untilIso = new Date(Date.now() + nudgeLeadDays * 86400000).toISOString();
    supabase
      .from("feedback_logs")
      .select("id", { count: "exact", head: true })
      .eq("merchant_id", merchant.id)
      .is("redeemed_at", null)
      .not("phone", "is", null)
      .not("transactional_consent_at", "is", null)
      .not("promo_expires_at", "is", null)
      .gte("promo_expires_at", nowIso)
      .lte("promo_expires_at", untilIso)
      .gte("star_rating", 3)
      .in("status", ["pending", "sent", "voucher_active"])
      .then(({ count }) => {
        if (!cancelled) setBurstCount(count ?? 0);
      });
    return () => {
      cancelled = true;
    };
  }, [merchant, rewardExpiryMonths, nudgeLeadDays]);



  const handleSave = async () => {
    if (!merchant) return;
    setSaving(true);

    const updates: Record<string, unknown> = {
      redemption_style: redemptionStyle,
      promo_activation_hours: activationHours,
      // Reminders are a core part of the loop — always on. Merchants who don't
      // want them set "no expiry" instead, which leaves nothing to remind about.
      nudge_enabled: true,
      nudge_lead_days: nudgeLeadDays,
      promo_discount_percent: initialDiscount,
      reward_expiry_months: rewardExpiryMonths,
      reward_quota_count: rewardQuotaCount,
      reward_quota_window_days: rewardQuotaWindowDays,
    };


    // Smart-regenerate localized strings tied to the initial discount %
    if (initialDiscount !== merchant.promo_discount_percent) {
      const defaults = getLocaleDefaults(merchant.locale);
      const newCta = regenerateIfTemplated(merchant.cta_text, defaults.getDiscountCta, initialDiscount);
      if (newCta !== null) updates.cta_text = newCta;
      const newSubtext = regenerateIfTemplated(merchant.discount_subtext, defaults.discountSubtext, initialDiscount);
      if (newSubtext !== null) updates.discount_subtext = newSubtext;
      const newEmailWhenReady = regenerateIfTemplated(merchant.email_when_ready_text, defaults.emailWhenReady, initialDiscount);
      if (newEmailWhenReady !== null) updates.email_when_ready_text = newEmailWhenReady;
    }

    const { error } = await supabase
      .from("merchants")
      .update(updates as any)
      .eq("id", merchant.id);

    if (error) {
      toast.error(t("discounts.saveFailed"));
    } else {
      toast.success(t("discounts.savedToast"));
      setMerchant({ ...merchant, ...updates } as Merchant);
    }
    setSaving(false);
  };


  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse text-muted-foreground">{t("discounts.loading")}</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className={`max-w-3xl mx-auto px-4 py-8 space-y-6 ${dirtyCount > 0 ? "pb-28" : ""}`}>
        <h1 className="text-xl font-display font-bold">{t("discounts.title")}</h1>

          {/* Card 0: Redemption Style */}
          <Card>
            <CardHeader>
              <CardTitle className="font-display text-lg">
                {t("discounts.redemption.title")}
                <ChangedBadge show={dirty.redemption} />
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-sm text-muted-foreground">
                {t("discounts.redemption.desc")}
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <button
                  onClick={() => setRedemptionStyle("quick_burn")}
                  className={`p-4 rounded-lg border-2 text-left transition-colors ${
                    redemptionStyle === "quick_burn"
                      ? "border-primary bg-primary/5"
                      : "border-border hover:border-muted-foreground/30"
                  }`}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <Flame size={18} className="text-primary" />
                    <span className="font-semibold text-sm">{t("discounts.redemption.quickBurn")}</span>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {t("discounts.redemption.quickBurnDesc")}
                  </p>
                </button>
                <button
                  onClick={() => setRedemptionStyle("verified_voucher")}
                  className={`p-4 rounded-lg border-2 text-left transition-colors ${
                    redemptionStyle === "verified_voucher"
                      ? "border-primary bg-primary/5"
                      : "border-border hover:border-muted-foreground/30"
                  }`}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <FileText size={18} className="text-primary" />
                    <span className="font-semibold text-sm">{t("discounts.redemption.voucher")}</span>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {t("discounts.redemption.voucherDesc")}
                  </p>
                </button>
              </div>
            </CardContent>
          </Card>

          {/* Card 1: Activation Delay */}
          <Card>
            <CardHeader>
              <CardTitle className="font-display text-lg flex items-center gap-2">
                <Clock size={18} /> {t("discounts.activation.title")}<ChangedBadge show={dirty.activation} />
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground">
                {t("discounts.activation.desc")}
              </p>
              <div className="rounded-md border border-primary/30 bg-primary/5 px-3 py-2 text-xs text-foreground">
                <span className="font-semibold text-primary">{t("discounts.activation.tipLabel")}</span>{" "}
                {t("discounts.activation.tipNote")}
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label>{t("discounts.activation.delay")}</Label>
                  <span className="text-sm font-semibold tabular-nums">
                    {activationHours === 0 ? t("discounts.activation.instant") : `${activationHours}h`}
                  </span>
                </div>
                <Slider
                  value={[activationHours]}
                  onValueChange={([v]) => setActivationHours(v)}
                  min={0}
                  max={72}
                  step={1}
                />
                <div className="flex justify-between text-[10px] text-muted-foreground">
                  <span>{t("discounts.activation.instant")}</span>
                  <span>{t("discounts.activation.max")}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Card 2: Follow-up Reminder */}
          <Card>
            <CardHeader>
              <CardTitle className="font-display text-lg flex items-center gap-2">
                <Bell size={18} /> {t("discounts.followup.title")}<ChangedBadge show={dirty.followup} />
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground">
                {t("discounts.followup.desc")}
              </p>
              <div className="rounded-md border border-primary/30 bg-primary/5 px-3 py-2 text-xs text-foreground">
                <span className="font-semibold text-primary">{t("discounts.followup.recommendedLabel")}</span>{" "}
                {t("discounts.followup.recommendedNote")}
              </div>

              <div className="space-y-2">
                <Label htmlFor="nudgeLeadDays" className="text-sm font-medium">
                  {t("discounts.followup.sendBefore")}
                </Label>
                <Select
                  value={String(nudgeLeadDays)}
                  onValueChange={(v) => setNudgeLeadDays(Number(v))}
                  disabled={rewardExpiryMonths === 0}
                >
                  <SelectTrigger id="nudgeLeadDays">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {NUDGE_LEAD_OPTIONS.map((d) => (
                      <SelectItem key={d} value={String(d)}>
                        {t(`onboarding.nudgeLeadLabel.${d}`, { defaultValue: `${d}d` })}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {burstCount !== null && burstCount > 0 && (
                  <div className="rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-foreground">
                    {t("discounts.followup.widenWarning", { count: burstCount })}
                  </div>
                )}
                <p className="text-xs text-muted-foreground">
                  {rewardExpiryMonths === 0
                    ? t("discounts.followup.noExpiryNote")
                    : t("discounts.followup.timingNote")}
                </p>
              </div>


            </CardContent>
          </Card>

          {/* Card 3: Discount */}
          <Card>
            <CardHeader>
              <CardTitle className="font-display text-lg flex items-center gap-2">
                <Percent size={18} /> {t("discounts.levels.title")}<ChangedBadge show={dirty.levels} />
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="initialDiscount">{t("discounts.levels.initial")}</Label>
                <p className="text-xs text-muted-foreground">{t("discounts.levels.initialDesc")}</p>
                <div className="flex items-center gap-2">
                  <Input
                    id="initialDiscount"
                    type="number"
                    min={1}
                    max={100}
                    value={initialDiscount}
                    onChange={(e) => setInitialDiscount(Number(e.target.value))}
                    className="w-24"
                  />
                  <span className="text-sm text-muted-foreground">%</span>
                </div>
              </div>
            </CardContent>

          </Card>

          {/* Card 4: Code Validity */}
          <Card>
            <CardHeader>
              <CardTitle className="font-display text-lg flex items-center gap-2">
                <CalendarDays size={18} /> {t("discounts.validity.title")}<ChangedBadge show={dirty.validity} />
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-5">
              <p className="text-sm text-muted-foreground">
                {t("discounts.validity.desc")}
              </p>
              <div className="space-y-2">
                <Label htmlFor="rewardExpiry">{t("discounts.validity.initial")}</Label>
                <Select value={String(rewardExpiryMonths)} onValueChange={(v) => setRewardExpiryMonths(Number(v))}>
                  <SelectTrigger id="rewardExpiry">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">{t("discounts.validity.expiry1m")}</SelectItem>
                    <SelectItem value="3">{t("discounts.validity.expiry3m")}</SelectItem>
                    <SelectItem value="6">{t("discounts.validity.expiry6m")}</SelectItem>
                    <SelectItem value="12">{t("discounts.validity.expiry12m")}</SelectItem>
                    <SelectItem value="0">{t("discounts.validity.expiryNone")}</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">{t("discounts.validity.hint")}</p>
              </div>

            </CardContent>
          </Card>

          {/* Card: Reward Frequency */}
          <Card>
            <CardHeader>
              <CardTitle className="font-display text-lg flex items-center justify-between gap-3">
                <span className="flex items-center gap-2">
                  <Timer size={18} /> {t("discounts.quota.title")}<ChangedBadge show={dirty.quota} />
                </span>
                <span className="inline-flex items-center rounded-full bg-primary/10 text-primary px-3 py-1 text-sm font-semibold tabular-nums">
                  {rewardQuotaCount === 0
                    ? t('onboarding.picker.unlimited')
                    : `${rewardQuotaCount} / ${t(`onboarding.windowLabel.${rewardQuotaWindowDays}`, { defaultValue: `${rewardQuotaWindowDays}d` })}`}
                </span>
              </CardTitle>
            </CardHeader>

            <CardContent className="space-y-5">
              <p className="text-sm text-muted-foreground">
                {t("discounts.quota.desc")}
              </p>
              <RewardQuotaPicker
                count={rewardQuotaCount}
                windowDays={rewardQuotaWindowDays}
                onChange={(c, w) => {
                  setRewardQuotaCount(c);
                  setRewardQuotaWindowDays(w);
                }}
              />
            </CardContent>
          </Card>
        </div>

      {/* Floating save bar — only visible when there are unsaved changes */}
      {dirtyCount > 0 && (
        <div className="fixed inset-x-0 bottom-0 z-40 border-t border-border bg-background/95 backdrop-blur-md">
          <div className="max-w-3xl mx-auto px-4 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] grid grid-cols-[minmax(0,1fr)_auto] items-center gap-3">
            <p className="min-w-0 truncate text-sm font-medium text-foreground">
              {dirtyCount === 1
                ? t("discounts.unsavedOne")
                : t("discounts.unsavedMany", { count: dirtyCount })}
            </p>
            <div className="flex shrink-0 items-center gap-2">
              <Button
                variant="ghost"
                onClick={() => merchant && applyMerchant(merchant)}
                disabled={saving}
              >
                {t("discounts.discard")}
              </Button>
              <AccessGatedAction>
                <Button
                  onClick={() => {
                    if (discountChanges.length > 0) {
                      setConfirmOpen(true);
                    } else {
                      handleSave();
                    }
                  }}
                  disabled={saving}
                >
                  {saving ? t("discounts.saving") : t("discounts.saveChanges")}
                </Button>
              </AccessGatedAction>
            </div>
          </div>
        </div>
      )}

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("discounts.levels.confirmTitle")}</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3">
                <p>{t("discounts.levels.confirmLead")}</p>
                <ul className="rounded-md border bg-muted/40 p-3 space-y-1.5 text-sm">
                  {discountChanges.map((c) => (
                    <li key={c.label} className="flex items-center justify-between gap-3">
                      <span className="text-foreground font-medium">{c.label}</span>
                      <span className="tabular-nums">
                        <span className="text-muted-foreground line-through">{c.from}%</span>
                        <span className="mx-2 text-muted-foreground">→</span>
                        <span className="font-semibold text-foreground">{c.to}%</span>
                      </span>
                    </li>
                  ))}
                </ul>
                <p className="text-xs text-muted-foreground">{t("discounts.levels.confirmNote")}</p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={saving}>{t("discounts.levels.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={async (e) => {
                e.preventDefault();
                await handleSave();
                setConfirmOpen(false);
              }}
              disabled={saving}
            >
              {saving ? t("discounts.saving") : t("discounts.levels.confirm")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );

}
